import { useState } from "react";
import { Header } from "@/components/Header";
import { getBookingByRef } from "@/lib/api";
import { formatDateLong } from "@/lib/format";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function LookupPage() {
  const [ref, setRef] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    try {
      setLoading(true);
      const res = await getBookingByRef(ref.trim().toUpperCase());
      setBooking(res);
    } catch (err) {
      setBooking(null);
      toast.error("Booking not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16">
        <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">
          Find booking
        </div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-8">Lookup by reference</h1>
        <form onSubmit={onSubmit} className="flex gap-3 mb-10" data-testid="lookup-form">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="COI-XXXXXXXX"
            className="flex-1 bg-white border-2 border-slate-900 p-3 font-mono-tight uppercase focus:outline-none focus:border-sky-500"
            data-testid="lookup-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 flex items-center gap-2 disabled:opacity-60"
            data-testid="lookup-submit"
          >
            <Search className="w-4 h-4" strokeWidth={3} /> Find
          </button>
        </form>

        {booking && (
          <div className="border-2 border-slate-900 bg-white brutal-shadow-lg p-8 animate-fade-up" data-testid="lookup-result">
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">
              {booking.reference}
            </div>
            <h2 className="font-display text-3xl uppercase mb-6">
              {formatDateLong(booking.date)} · {booking.time}
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Info label="School">{booking.school_name}</Info>
              <Info label="School address">{booking.school_address || "—"}</Info>
              <Info label="Teacher">{booking.teacher_name}</Info>
              <Info label="Email">{booking.email}</Info>
              <Info label="Phone">{booking.phone}</Info>
              <Info label="County">{booking.county || "—"}</Info>
              <Info label="Group size">{booking.group_size}</Info>
              <Info label="Age group">{booking.age_group}</Info>
              {booking.notes && (
                <div className="md:col-span-2">
                  <Info label="Notes">{booking.notes}</Info>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div>
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
    <div className="text-slate-900">{children}</div>
  </div>
);
